import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Theme {
    colors: {
      main: React.CSSProperties['color'];
      secondary: React.CSSProperties['color'];
      dark: React.CSSProperties['color'];
      ash: React.CSSProperties['color'];
      dust: React.CSSProperties['color'];
      paper: React.CSSProperties['color'];
      light: React.CSSProperties['color'];
      pinky: React.CSSProperties['color'];
      grass: React.CSSProperties['color'];
      fog: React.CSSProperties['color'];
      grey: React.CSSProperties['color'];
      white: React.CSSProperties['color'];
      nightSky: React.CSSProperties['color'];
    };
  }

  interface Palette {
    primary: Palette['primary'];
    secondary: Palette['secondary'];
    text: Palette['text'];
  }

  interface PaletteColor {
    darker?: string;
    main: string;
    secondary: string;
    dark: string;
    ash: string;
    dust: string;
    paper: string;
    light: string;
    pinky: string;
    grass: string;
    fog: string;
    grey: string;
    white: string;
    nightSky: string;
  }

  interface ThemeOptions {
    colors: {
      main: React.CSSProperties['color'];
      secondary: React.CSSProperties['color'];
      dark: React.CSSProperties['color'];
      ash: React.CSSProperties['color'];
      dust: React.CSSProperties['color'];
      paper: React.CSSProperties['color'];
      light: React.CSSProperties['color'];
      pinky: React.CSSProperties['color'];
      grass: React.CSSProperties['color'];
      fog: React.CSSProperties['color'];
      grey: React.CSSProperties['color'];
      white: React.CSSProperties['color'];
      nightSky: React.CSSProperties['color'];
    };
  }
}

const pathToFonts = 'src/public/satoshi_font/fonts/';

export const theme = createTheme({
  colors: {
    main: '#574CE6',
    secondary: '#FFF',
    dark: '#373185',
    ash: '#4F5863',
    dust: '#B9B9C2',
    paper: '#F5F6FA',
    light: '#EEEDFC',
    pinky: '#DDDBFA',
    grass: '#004615',
    fog: '#E8E8EB',
    grey: '#68687A',
    white: '#FFF',
    nightSky: '#2A2847',
  },
  palette: {
    primary: {
      main: '#004615',
    },
    secondary: {
      main: '#FFF',
    },
    text: {
      primary: '#4F5863',
    },
  },
  typography: {
    fontFamily: 'Satoshi, sans-serif',
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#004615', // Custom color for AppBar
          color: '#FFF', // Text color in AppBar
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: `
      @font-face {
        font-family: 'Satoshi-Variable';
        src: url('${pathToFonts}Satoshi-Variable.woff2') format('woff2'),
             url('${pathToFonts}Satoshi-Variable.woff') format('woff'),
             url('${pathToFonts}Satoshi-Variable.ttf') format('truetype');
             font-weight: 300 900;
             font-display: swap;
             font-style: normal;
      }

      @font-face {
        font-family: 'Satoshi-VariableItalic';
        src: url('${pathToFonts}Satoshi-VariableItalic.woff2') format('woff2'),
             url('${pathToFonts}Satoshi-VariableItalic.woff') format('woff'),
             url('${pathToFonts}Satoshi-VariableItalic.ttf') format('truetype');
             font-weight: 300 900;
             font-display: swap;
             font-style: italic;
      }

      @font-face {
        font-family: 'Satoshi-Light';
        src: url('${pathToFonts}Satoshi-Light.woff2') format('woff2'),
             url('${pathToFonts}Satoshi-Light.woff') format('woff'),
             url('${pathToFonts}Satoshi-Light.ttf') format('truetype');
             font-weight: 300;
             font-display: swap;
             font-style: normal;
      }

      @font-face {
        font-family: 'Satoshi-LightItalic';
        src: url('${pathToFonts}Satoshi-LightItalic.woff2') format('woff2'),
             url('${pathToFonts}Satoshi-LightItalic.woff') format('woff'),
             url('${pathToFonts}Satoshi-LightItalic.ttf') format('truetype');
             font-weight: 300;
             font-display: swap;
             font-style: italic;
      }

      @font-face {
        font-family: 'Satoshi-Regular';
        src: url('${pathToFonts}Satoshi-Regular.woff2') format('woff2'),
             url('${pathToFonts}Satoshi-Regular.woff') format('woff'),
             url('${pathToFonts}Satoshi-Regular.ttf') format('truetype');
             font-weight: 400;
             font-display: swap;
             font-style: normal;
      }

      @font-face {
        font-family: 'Satoshi-Italic';
        src: url('${pathToFonts}Satoshi-Italic.woff2') format('woff2'),
             url('${pathToFonts}Satoshi-Italic.woff') format('woff'),
             url('${pathToFonts}Satoshi-Italic.ttf') format('truetype');
             font-weight: 400;
             font-display: swap;
             font-style: italic;
      }

      @font-face {
        font-family: 'Satoshi-Medium';
        src: url('${pathToFonts}Satoshi-Medium.woff2') format('woff2'),
             url('${pathToFonts}Satoshi-Medium.woff') format('woff'),
             url('${pathToFonts}Satoshi-Medium.ttf') format('truetype');
             font-weight: 500;
             font-display: swap;
             font-style: normal;
      }

      @font-face {
        font-family: 'Satoshi-MediumItalic';
        src: url('${pathToFonts}Satoshi-MediumItalic.woff2') format('woff2'),
             url('${pathToFonts}Satoshi-MediumItalic.woff') format('woff'),
             url('${pathToFonts}Satoshi-MediumItalic.ttf') format('truetype');
             font-weight: 500;
             font-display: swap;
             font-style: italic;
      }

      @font-face {
        font-family: 'Satoshi-Bold';
        src: url('${pathToFonts}Satoshi-Bold.woff2') format('woff2'),
             url('${pathToFonts}Satoshi-Bold.woff') format('woff'),
             url('${pathToFonts}Satoshi-Bold.ttf') format('truetype');
             font-weight: 700;
             font-display: swap;
             font-style: normal;
      }

      @font-face {
        font-family: 'Satoshi-BoldItalic';
        src: url('${pathToFonts}Satoshi-BoldItalic.woff2') format('woff2'),
             url('${pathToFonts}Satoshi-BoldItalic.woff') format('woff'),
             url('${pathToFonts}Satoshi-BoldItalic.ttf') format('truetype');
             font-weight: 700;
             font-display: swap;
             font-style: italic;
      }

      @font-face {
        font-family: 'Satoshi-Black';
        src: url('${pathToFonts}Satoshi-Black.woff2') format('woff2'),
             url('${pathToFonts}Satoshi-Black.woff') format('woff'),
             url('${pathToFonts}Satoshi-Black.ttf') format('truetype');
             font-weight: 900;
             font-display: swap;
             font-style: normal;
      }

      @font-face {
        font-family: 'Satoshi-BlackItalic';
        src: url('${pathToFonts}Satoshi-BlackItalic.woff2') format('woff2'),
             url('${pathToFonts}Satoshi-BlackItalic.woff') format('woff'),
             url('${pathToFonts}Satoshi-BlackItalic.ttf') format('truetype');
             font-weight: 900;
             font-display: swap;
             font-style: italic;
      }`,
    },
  },
});
